// src/components/CuratedFikaEntrepreneursConfirm.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircleIcon,
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  UsersIcon,
  RocketLaunchIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  BriefcaseIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';

const formatSessionDate = (dateString, timezone = 'Asia/Tokyo') => {
  if (!dateString) return 'Date not available';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: timezone
    });
  } catch (error) {
    return 'Date error';
  }
};

const formatSessionTime = (dateString, timezone = 'Asia/Tokyo') => {
  if (!dateString) return 'Time not available';
  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezone
    });
  } catch (error) {
    return 'Time error';
  }
};

const getFounderRoleIcon = (role) => {
  const icons = {
    'Solo Founder': '🏃',
    'Co-Founder': '👥',
    'Solopreneur': '💼',
    'Freelancer': '🎨',
    'Early-Stage Operator': '🚀',
    'Growth-Stage Operator': '📈',
    'Serial Entrepreneur': '🎯',
    'First-Time Founder': '🌱'
  };
  return icons[role] || '🏗️';
};

const CuratedFikaEntrepreneursConfirm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const sessionId = searchParams.get('session');
  const participantId = searchParams.get('participant');
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [participantData, setParticipantData] = useState(null);
  const [otherFounder, setOtherFounder] = useState(null);
  const [error, setError] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setError('Invalid confirmation link - session ID missing');
      setLoading(false);
      return;
    }
    loadSessionDetails();
  }, [sessionId, participantId, token]);

  const loadSessionDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const requestBody = {
        action: 'get_session_details',
        sessionId: sessionId
      };

      if (participantId) {
        requestBody.participantId = participantId;
      }
      if (token) {
        requestBody.token = token;
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/curated-fika-entrepreneurs-confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();

      if (result.success && result.data) {
        setSessionData(result.data.session);
        setParticipantData(result.data.participant);
        setOtherFounder(result.data.otherFounder);
        
        if (result.data.participant?.participation_status === 'confirmed') {
          setConfirmed(true);
        }
      } else {
        throw new Error(result.error || 'Failed to load session details');
      }

    } catch (error) {
      console.error('Error loading session details:', error);
      setError(error.message || 'Failed to load session details');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAttendance = async () => {
    if (!sessionId) {
      setError('Missing session ID');
      return;
    }

    try {
      setConfirming(true);
      setError(null);

      const participantIdentifier = participantId || 
                                  participantData?.application_id || 
                                  participantData?.id;
      
      if (!participantIdentifier) {
        throw new Error('Cannot identify participant for confirmation');
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/curated-fika-entrepreneurs-confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          action: 'confirm_attendance',
          sessionId: sessionId,
          participantId: participantIdentifier
        })
      });

      const result = await response.json();

      if (result.success) {
        setConfirmed(true);
        
        setParticipantData(prev => ({
          ...prev,
          participation_status: 'confirmed',
          confirmed_at: new Date().toISOString()
        }));

        if (result.data && result.data.session) {
          setSessionData(result.data.session);
        } else if (!result.data?.alreadyConfirmed) {
          setSessionData(prev => ({
            ...prev,
            current_participants: (prev.current_participants || 0) + 1
          }));
        }
        
      } else {
        throw new Error(result.error || 'Failed to confirm attendance');
      }

    } catch (error) {
      console.error('Error confirming attendance:', error);
      setError(error.message || 'Failed to confirm attendance');
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your Founders 1-on-1 session...</p>
        </div>
      </div>
    );
  }

  if (error && !sessionData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-50 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Unable to Load Session
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors"
          >
            Return to Homepage
          </button>
        </div>
      </div>
    );
  }

  if (confirmed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center px-6">
        <div className="max-w-2xl w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-xl p-8 text-center"
          >
            <div className="flex justify-center mb-8 mt-4">
              <CheckCircleSolid className="w-20 h-20 text-green-500" />
            </div>
            
            <h1 className="text-3xl font-bold text-gray-900 mb-6">
              Attendance Confirmed!
            </h1>
            
            <p className="text-gray-600 mb-8 text-lg">
              Thank you for confirming. We're excited to facilitate this meaningful founder-to-founder connection.
            </p>

            {sessionData && (
              <div className="bg-orange-50 rounded-xl p-6 mb-8 text-left">
                <h3 className="text-lg font-semibold text-orange-800 mb-4 flex items-center">
                  <RocketLaunchIcon className="w-5 h-5 mr-2" />
                  Your 1-on-1 Session Details:
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center">
                    <BriefcaseIcon className="w-5 h-5 text-orange-600 mr-3" />
                    <span className="font-medium">{sessionData.session_title}</span>
                  </div>
                  <div className="flex items-center">
                    <CalendarIcon className="w-5 h-5 text-orange-600 mr-3" />
                    <span>{formatSessionDate(sessionData.session_date, sessionData.venue_timezone)}</span>
                  </div>
                  <div className="flex items-center">
                    <ClockIcon className="w-5 h-5 text-orange-600 mr-3" />
                    <span>{formatSessionTime(sessionData.session_date, sessionData.venue_timezone)}</span>
                  </div>
                  <div className="flex items-center">
                    <MapPinIcon className="w-5 h-5 text-orange-600 mr-3" />
                    <div>
                      <div className="font-medium">{sessionData.venue_name}</div>
                      <div className="text-gray-600">{sessionData.venue_address}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {otherFounder && (
              <div className="bg-blue-50 rounded-xl p-6 mb-8 text-left">
                <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
                  <UsersIcon className="w-5 h-5 mr-2" />
                  You'll be meeting with:
                </h4>
                <div className="text-sm">
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-3">{getFounderRoleIcon(otherFounder.founder_role)}</span>
                    <div>
                      <div className="font-semibold text-gray-900">
                        {otherFounder.first_name} {otherFounder.last_name}
                      </div>
                      <div className="text-gray-600">{otherFounder.founder_role}</div>
                    </div>
                  </div>
                  <div className="mt-3 space-y-1 text-gray-700">
                    <div><strong>Stage:</strong> {otherFounder.company_stage}</div>
                    {otherFounder.industry && <div><strong>Industry:</strong> {otherFounder.industry}</div>}
                  </div>
                </div>
              </div>
            )}

            <div className="bg-purple-50 rounded-xl p-6 mb-8">
              <h4 className="font-semibold text-purple-800 mb-3">What happens next?</h4>
              <ul className="text-left text-sm text-purple-700 space-y-2">
                <li>• You'll receive a reminder email 24 hours before the session</li>
                <li>• Both founders will receive final venue details once confirmed</li>
                <li>• Arrive 5-10 minutes early for a relaxed start</li>
                <li>• Come prepared to share openly and listen deeply</li>
              </ul>
            </div>

            <div className="flex justify-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Return Home
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 py-12 px-6">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden"
        >
          <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white p-8">
            <div className="flex items-center justify-center mb-4">
              <RocketLaunchIcon className="w-8 h-8 mr-3" />
              <h1 className="text-3xl font-bold">Founders 1-on-1 Invitation</h1>
            </div>
            <p className="text-center text-orange-100">
              You've been matched for an intimate founder-to-founder conversation
            </p>
          </div>

          {sessionData && (
            <div className="p-8">
              <div className="bg-orange-50 border-l-4 border-orange-600 p-4 mb-6">
                <p className="text-sm text-orange-800">
                  <strong>🎯 Exclusive 1-on-1:</strong> This is an intimate session with just one other founder.
                </p>
              </div>

              <div className="bg-orange-50 rounded-xl p-6 mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Session Details</h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <BriefcaseIcon className="w-6 h-6 text-orange-600 mr-3 mt-1" />
                      <div>
                        <div className="font-semibold text-gray-900">Session</div>
                        <div className="text-gray-600">{sessionData.session_title}</div>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <CalendarIcon className="w-6 h-6 text-orange-600 mr-3 mt-1" />
                      <div>
                        <div className="font-semibold text-gray-900">Date</div>
                        <div className="text-gray-600">
                          {formatSessionDate(sessionData.session_date, sessionData.venue_timezone)}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <ClockIcon className="w-6 h-6 text-orange-600 mr-3 mt-1" />
                      <div>
                        <div className="font-semibold text-gray-900">Time</div>
                        <div className="text-gray-600">
                          {formatSessionTime(sessionData.session_date, sessionData.venue_timezone)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start">
                      <MapPinIcon className="w-6 h-6 text-orange-600 mr-3 mt-1" />
                      <div>
                        <div className="font-semibold text-gray-900">Venue</div>
                        <div className="text-gray-600">
                          <div className="font-medium">{sessionData.venue_name}</div>
                          <div className="text-sm">{sessionData.venue_address}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {otherFounder && (
                <div className="bg-blue-50 rounded-xl p-6 mb-8">
                  <h3 className="text-lg font-semibold text-blue-800 mb-4 flex items-center">
                    <LightBulbIcon className="w-5 h-5 mr-2" />
                    You'll be meeting with:
                  </h3>
                  <div className="bg-white rounded-lg p-5 border-2 border-blue-200">
                    <div className="flex items-center mb-4">
                      <span className="text-3xl mr-4">{getFounderRoleIcon(otherFounder.founder_role)}</span>
                      <div>
                        <h4 className="font-bold text-gray-900 text-lg">
                          {otherFounder.first_name} {otherFounder.last_name.charAt(0)}.
                        </h4>
                        <p className="text-sm text-gray-600">{otherFounder.founder_role}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div><strong>Stage:</strong> {otherFounder.company_stage}</div>
                      {otherFounder.industry && <div><strong>Industry:</strong> {otherFounder.industry}</div>}
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                  <div className="flex items-center">
                    <XCircleIcon className="w-5 h-5 text-red-500 mr-2" />
                    <span className="text-red-800 text-sm">{error}</span>
                  </div>
                </div>
              )}

              <div className="text-center">
                <button
                  onClick={handleConfirmAttendance}
                  disabled={confirming}
                  className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:from-orange-700 hover:to-red-700 disabled:opacity-50 transition-all"
                >
                  {confirming ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                      Confirming...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <CheckCircleIcon className="w-6 h-6 mr-3" />
                      Confirm My Attendance
                    </div>
                  )}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default CuratedFikaEntrepreneursConfirm;